import { useId, useState, type FormEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Ban, Loader2, SquarePen, X } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { EnumSelect } from '@/components/ui/enum-select'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field'
import { FormDialog, FormDialogContent, FormDialogTrigger } from '@/components/ui/form-dialog'
import { Input } from '@/components/ui/input'
import { apiClient } from '@/lib/api/client'
import type { Consignment, Inquiry } from '@/lib/api/types'
import {
  canCancelInquiry,
  consignmentQuickPatch,
  consignmentStatusChoices,
  inquiryQuickPatch,
  inquiryStatusChoices,
  quickEditChange,
  quickEditedFields,
  quickEditForm,
  type QuickEditChange,
  type QuickEditRow,
  type StatusChoice,
} from '@/lib/quick-edit'
import { statusLabel } from '@/lib/status-tone'
import { HelmOwnerCombobox } from '@/src/components/HelmOwnerCombobox'
import type { OptimisticRows } from '@/src/hooks/useOptimisticRows'
import { inquiryTitle } from '@/src/pages/inquirySummary'
import { DueDateNotice } from '@/src/components/DueDateNotice'
import { CancelInquiryDialog } from '@/src/components/CancelInquiryDialog'
import { shipmentReference } from '@/lib/customer-messages'

interface QuickEditDialogProps {
  /** Names the record in the dialog and on the button. */
  label: string
  /** The full record, where everything else is edited. */
  href: string
  row: QuickEditRow & { operational_state?: string }
  choices: StatusChoice[]
  saving: boolean
  onSave: (change: QuickEditChange) => void
  /** More for this kind of record, below the fields; `close` shuts the dialog. */
  actions?: (close: () => void) => ReactNode
}

/**
 * Owner, status and due date for one row, without opening the record. The
 * change shows on the row as soon as it is saved; the list puts it back if
 * Helm refuses it.
 */
export function QuickEditDialog({
  label,
  href,
  row,
  choices,
  saving,
  onSave,
  actions,
}: QuickEditDialogProps) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(() => quickEditForm(row))
  const chosen = choices.find((choice) => choice.status === form.status)
  const alsoMarks =
    chosen && form.status !== row.status && chosen.operational_state !== row.operational_state
      ? chosen.operational_state
      : null

  function openChange(next: boolean) {
    // Start from the row as it is now, not as it was the last time.
    if (next) setForm(quickEditForm(row))
    setOpen(next)
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    const change = quickEditChange(row, form, choices)
    setOpen(false)
    if (Object.keys(change).length > 0) onSave(change)
  }

  return (
    <FormDialog open={open} onOpenChange={openChange}>
      <FormDialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`Quick edit ${label}`}
          title="Quick edit: owner, status, due date"
          disabled={saving}
        >
          {saving ? <Loader2 className="animate-spin" /> : <SquarePen />}
        </Button>
      </FormDialogTrigger>
      <FormDialogContent
        size="sm"
        title="Quick edit"
        description={label}
        submitLabel="Save"
        onSubmit={submit}
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor={`${id}-owner`}>Owner</FieldLabel>
            <HelmOwnerCombobox
              id={`${id}-owner`}
              value={form.ownerUserId}
              onValueChange={(ownerUserId) => setForm({ ...form, ownerUserId })}
              placeholder="Unassigned"
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-status`}>Status</FieldLabel>
            <EnumSelect
              value={form.status}
              onValueChange={(status) => setForm({ ...form, status })}
              options={choices.map((choice) => ({
                value: choice.status,
                label: statusLabel(choice.status),
              }))}
              triggerProps={{ id: `${id}-status`, 'aria-describedby': `${id}-status-help` }}
            />
            <FieldDescription id={`${id}-status-help`}>
              {alsoMarks
                ? `This also marks the work ${statusLabel(alsoMarks).toLowerCase()}.`
                : 'Only the moves Helm allows from here are listed.'}
            </FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-due`}>Due date</FieldLabel>
            <div className="flex gap-2">
              <Input
                id={`${id}-due`}
                type="date"
                value={form.dueDate}
                onChange={(event) => setForm({ ...form, dueDate: event.target.value })}
                aria-describedby={`${id}-due-help`}
                className="min-w-0 flex-1"
              />
              {form.dueDate && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForm({ ...form, dueDate: '' })}
                >
                  Clear
                </Button>
              )}
            </div>
            <FieldDescription id={`${id}-due-help`}>
              Due by 17:00 Kampala time, unless it already has a time.
            </FieldDescription>
          </Field>
        </FieldGroup>
        <p className="text-sm text-muted-foreground">
          Priority, tasks and everything else are on the{' '}
          <Link to={href} className="font-medium text-foreground underline underline-offset-4">
            full record
          </Link>
          .
        </p>
        {actions?.(() => setOpen(false))}
      </FormDialogContent>
    </FormDialog>
  )
}

/** A due date that moved, waiting on whether to tell the customer. */
interface MovedDueDate {
  previousDueAt: string | null
  dueAt: string
}

/**
 * Saves a quick edit, and once Helm has a new due date, offers to tell the
 * customer. A cleared due date has nothing to tell them.
 */
function useSaveAndOffer() {
  const [moved, setMoved] = useState<MovedDueDate | null>(null)
  async function saveAndOffer(
    previousDueAt: string | null | undefined,
    change: QuickEditChange,
    save: () => Promise<boolean>,
  ) {
    if ((await save()) && change.sla_due_at) {
      setMoved({ previousDueAt: previousDueAt ?? null, dueAt: change.sla_due_at })
    }
  }
  return { moved, dismiss: () => setMoved(null), saveAndOffer }
}

export function InquiryQuickEdit({
  inquiry,
  edits,
}: {
  inquiry: Inquiry
  edits: OptimisticRows<Inquiry>
}) {
  const label = inquiryTitle(inquiry)
  const { moved, dismiss, saveAndOffer } = useSaveAndOffer()
  const [cancelling, setCancelling] = useState(false)
  return (
    <>
      <QuickEditDialog
        label={label}
        href={`/inquiries/${inquiry.id}`}
        row={inquiry}
        choices={inquiryStatusChoices(inquiry)}
        saving={edits.saving.has(inquiry.id)}
        actions={(close) =>
          canCancelInquiry(inquiry) ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="self-start"
              onClick={() => {
                close()
                setCancelling(true)
              }}
            >
              <Ban />
              Cancel inquiry…
            </Button>
          ) : inquiry.operational_state === 'CANCELLED' ? (
            <p className="text-sm text-muted-foreground">
              It is cancelled; an admin can reopen it from its page.
            </p>
          ) : null
        }
        onSave={(change) =>
          void saveAndOffer(inquiry.sla_due_at, change, () =>
            edits.save(
              inquiry,
              change as Partial<Inquiry>,
              () =>
                apiClient
                  .updateInquiry(inquiry.id, inquiryQuickPatch(change))
                  .then(quickEditedFields),
              label,
            ),
          )
        }
      />
      {cancelling && (
        <CancelInquiryDialog
          open
          onOpenChange={(open) => !open && setCancelling(false)}
          inquiry={inquiry}
          label={label}
          onConfirm={async (reason) => {
            // Shown on the row at once; put back, with why, if Helm refuses.
            void edits.save(
              inquiry,
              { status: 'CANCELLED', operational_state: 'CANCELLED' } as Partial<Inquiry>,
              () => apiClient.cancelInquiry(inquiry.id, reason).then(quickEditedFields),
              label,
            )
          }}
        />
      )}
      {moved && (
        <DueDateNotice
          open
          onOpenChange={(open) => !open && dismiss()}
          record={{
            kind: 'inquiry',
            id: inquiry.id,
            customerId: inquiry.customer_id,
            customer: inquiry.customer,
            reference: label,
          }}
          previousDueAt={moved.previousDueAt}
          dueAt={moved.dueAt}
        />
      )}
    </>
  )
}

/**
 * How Helm names a consignment to staff: its code and title,
 * "XTLCSEA202610180001 (Baby food processor)", as messages to the customer do.
 * Never a tracking number, which names a leg of the journey, not the consignment.
 */
export function consignmentLabel(consignment: Pick<Consignment, 'id' | 'code' | 'title'>) {
  return shipmentReference(consignment) || `Consignment ${consignment.id.slice(0, 8)}`
}

export function ConsignmentQuickEdit({
  consignment,
  edits,
}: {
  consignment: Consignment
  edits: OptimisticRows<Consignment>
}) {
  const label = consignmentLabel(consignment)
  const { moved, dismiss, saveAndOffer } = useSaveAndOffer()
  return (
    <>
      <QuickEditDialog
        label={label}
        href={`/consignments/${consignment.id}`}
        row={consignment}
        choices={consignmentStatusChoices(consignment)}
        saving={edits.saving.has(consignment.id)}
        onSave={(change) =>
          void saveAndOffer(consignment.sla_due_at, change, () =>
            edits.save(
              consignment,
              change as Partial<Consignment>,
              () =>
                apiClient
                  .updateConsignment(consignment.id, consignmentQuickPatch(change))
                  .then(quickEditedFields),
              label,
            ),
          )
        }
      />
      {moved && (
        <DueDateNotice
          open
          onOpenChange={(open) => !open && dismiss()}
          record={{
            kind: 'consignment',
            id: consignment.id,
            customerId: consignment.customer_id,
            customer: consignment.customer,
            // The customer knows it by its reference and title, not a package number.
            reference: shipmentReference(consignment),
          }}
          previousDueAt={moved.previousDueAt}
          dueAt={moved.dueAt}
        />
      )}
    </>
  )
}

/** Why the last quick edit on a list was not saved, until it is dismissed. */
export function QuickEditFailure({
  edits,
}: {
  edits: Pick<OptimisticRows<{ id: string }>, 'failure' | 'dismissFailure'>
}) {
  if (!edits.failure) return null
  return (
    <Alert variant="destructive" className="pr-12">
      <AlertDescription>{edits.failure}</AlertDescription>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Dismiss"
        className="absolute top-2 right-2"
        onClick={edits.dismissFailure}
      >
        <X />
      </Button>
    </Alert>
  )
}
